import { RtoSettingsOrgTermsEditor } from "@/components/ops/RtoSettingsOrgTermsEditor";
import { useRtoSettingsSection } from "@/components/ops/useRtoSettingsSection";
import {
  cardMutedTextClass,
  cardTitleTextClass,
  compactInputClass,
  insetSurfaceCardClass,
} from "@/components/common/surfaceClasses";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export function RtoSettingsSection({ organizationId }: { organizationId: string }) {
  const vm = useRtoSettingsSection(organizationId);

  return (
    <section className="space-y-4">
      {vm.msg ? (
        <div
          className={`text-sm rounded-lg px-3 py-2 border ${
            vm.msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/35 dark:border-emerald-800 dark:text-emerald-200" : "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/35 dark:border-red-800 dark:text-red-300"
          }`}
        >
          {vm.msg.text}
        </div>
      ) : null}
      {!(vm.manufacturerOptions.length > 0 && vm.useManufacturerTerms) ? (
        <div className={insetSurfaceCardClass}>
          <p className={`text-xs font-semibold ${cardTitleTextClass}`}>Sales Console RTO calculator title</p>
          <p className={`mt-1 text-[11px] ${cardMutedTextClass}`}>
            Enter the <strong>RTO provider name</strong> only (e.g. your org or a manufacturer). Sales Console shows{" "}
            <span className="font-mono text-[10px]">{"{name} RTO Terms Calculator"}</span>. Leave blank to inherit the manufacturer
            display name when “Use manufacturer RTO terms” is enabled.
          </p>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <input
              className={`w-full text-sm ${compactInputClass}`}
              placeholder="RTO Provider Name"
              value={vm.salesConsoleRtoTitle}
              onChange={(e) => vm.setSalesConsoleRtoTitle(e.target.value)}
            />
            <button
              type="button"
              onClick={() => void vm.saveSalesConsoleRtoTitle()}
              disabled={vm.saving}
              className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 whitespace-nowrap disabled:opacity-50"
            >
              Save RTO title
            </button>
          </div>
        </div>
      ) : null}
      <div className="flex items-center justify-between mb-2">
        <div className="w-full text-center">
          <h3 className={`text-sm font-semibold ${cardTitleTextClass}`}>RTO Term Definitions</h3>
          <p className={`text-xs mt-0.5 ${cardMutedTextClass}`}>Sales Console pulls these term rows. Divisor and required down are managed here.</p>
        </div>
      </div>
      {vm.manufacturerOptions.length > 0 ? (
        <div
          {...{ [FIREFLY_TARGET_ATTR]: "rto-mfg-inherit" }}
          className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={vm.useManufacturerTerms} onChange={(e) => vm.setUseManufacturerTerms(e.target.checked)} />
            Use{" "}
            {vm.manufacturerOptions.length === 1
              ? `${vm.manufacturerOptions[0]?.name}'s`
              : "manufacturer"}{" "}
            RTO terms for calculator
          </label>
          {vm.manufacturerOptions.length > 1 ? (
            <div className="mt-2">
              <select
                className={`rounded-md text-xs border-amber-300 ${compactInputClass}`}
                value={vm.manufacturerSourceId}
                onChange={(e) => vm.setManufacturerSourceId(e.target.value)}
              >
                {vm.manufacturerOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {!vm.useManufacturerTerms ? (
            <p className="mt-2 rounded border border-amber-300 bg-white px-2 py-1 text-[11px] dark:border-amber-800 dark:bg-zinc-900/50 dark:text-amber-100">
              Warning: users will not have RTO terms until org terms are defined below.
            </p>
          ) : null}
        </div>
      ) : null}
      {!vm.useManufacturerTerms ? (
        <RtoSettingsOrgTermsEditor
          rtoTerms={vm.rtoTerms}
          setRtoTerms={vm.setRtoTerms}
          rtoStates={vm.rtoStates}
          rtoStateInput={vm.rtoStateInput}
          setRtoStateInput={vm.setRtoStateInput}
          showRtoStateEditor={vm.showRtoStateEditor}
          setShowRtoStateEditor={vm.setShowRtoStateEditor}
          saving={vm.saving}
          addRtoState={vm.addRtoState}
          removeRtoState={vm.removeRtoState}
          openStateOverridesByTerm={vm.openStateOverridesByTerm}
          setOpenStateOverridesByTerm={vm.setOpenStateOverridesByTerm}
          openRtoActionsFor={vm.openRtoActionsFor}
          setOpenRtoActionsFor={vm.setOpenRtoActionsFor}
        />
      ) : null}
      <div className="mt-2">
        <button
          type="button"
          {...{ [FIREFLY_TARGET_ATTR]: "rto-terms-save" }}
          onClick={() => void vm.saveRtoTerms()}
          disabled={vm.saving}
          className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          Save RTO terms
        </button>
      </div>
    </section>
  );
}
